import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import {
  CANADA_REGION_CODES,
  EQUIPMENT_CONDITION,
  FINANCING_TYPE,
  LEGAL_STRUCTURE,
  US_REGION_CODES,
  FinanceProgramType,
  PaymentPeriodTiming,
  FinanceProgramRateType,
  SubsidyCapType,
  MarkupType,
} from '@trnsact/trnsact-shared-types';
import { CriteriaValue } from 'modules/jsonRulesCriterias/businessCriteria/types';
import { convertFactToSelectOptions } from 'modules/jsonRulesCriterias/businessCriteria';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { FACT_NAMES_BOOLEAN, FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { FinanceProgramForm } from '../schema';
import { AllRules, FinanceProgramFormSteps, InsuranceLabelsByKey, MarkupMethods, Structures } from '../types';

export const rateTypeOptions = [
  { label: 'Interest Rate', value: FinanceProgramRateType.InterestRate },
  { label: 'Rate Factor', value: FinanceProgramRateType.RateFactor },
];

export const structureOptions = [
  { label: 'Markup', value: Structures.Markup },
  { label: 'Subsidy', value: Structures.Subsidy },
  { label: 'None', value: Structures.None },
];

export const markupOptions = [
  { label: 'Markup %', value: MarkupMethods.Markup },
  { label: 'Customer Rate', value: MarkupMethods.CustomerRate },
];

export const programTypeOptions = [
  { label: 'Loan', value: FinanceProgramType.Loan },
  { label: 'Lease', value: FinanceProgramType.Lease },
  { label: 'EFA', value: FinanceProgramType.Efa },
];

export const paymentCalculationMethodOptions = [
  { label: 'Advance', value: PaymentPeriodTiming.BeginningOfPeriod },
  { label: 'Arrears', value: PaymentPeriodTiming.EndOfPeriod },
];

export const termsOptions = [
  { label: '12', value: '12' },
  { label: '24', value: '24' },
  { label: '36', value: '36' },
  { label: '48', value: '48' },
  { label: '60', value: '60' },
  { label: '72', value: '72' },
  { label: '84', value: '84' },
  { label: '96', value: '96' },
  { label: '108', value: '108' },
  { label: '120', value: '120' },
];

export const booleanOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

export const capTypeOptions = [
  { label: 'Dealer Net Invoice %', value: SubsidyCapType.NetInvoicePercentage },
  { label: 'Fixed $ Amount', value: SubsidyCapType.FixedAmount },
];

export const getInitialCriteriaConditionFormValue = (fact: AllRules): CriteriaValue => ({
  fact,
  number: { min: null, max: null },
  boolean: { value: false },
  string: { value: [] },
  isRequiredToQuote: false,
});

export const factNamesNumeric = convertFactToSelectOptions(FACT_NAMES_NUMERIC);
export const factNamesBoolean = convertFactToSelectOptions(FACT_NAMES_BOOLEAN);
export const factNamesString = convertFactToSelectOptions(FACT_NAMES_STRING);

export const allFacts = [...factNamesNumeric, ...factNamesString, ...factNamesBoolean];

export const triggerPathByStep: Record<FinanceProgramFormSteps, UseControllerProps<FinanceProgramForm>['name']> = {
  [FinanceProgramFormSteps.First]: 'program.first',
  [FinanceProgramFormSteps.Second]: 'program.second',
  [FinanceProgramFormSteps.Third]: 'program.third',
  [FinanceProgramFormSteps.Fourth]: 'program.fourth',
  [FinanceProgramFormSteps.Fifth]: 'program.fifth',
  [FinanceProgramFormSteps.Sixth]: 'program.sixth',
};

const equipmentConditionOptions = convertEnumToSelectOptions(EQUIPMENT_CONDITION);
const financingTypeOptions = convertEnumToSelectOptions(FINANCING_TYPE);
const legalStructureOptions = convertEnumToSelectOptions(LEGAL_STRUCTURE);
const canadaRegionOptions = convertEnumToSelectOptions(CANADA_REGION_CODES);
const usRegionOptions = convertEnumToSelectOptions(US_REGION_CODES);

export const allStringFactsOptions = [
  ...equipmentConditionOptions,
  ...financingTypeOptions,
  ...legalStructureOptions,
  ...canadaRegionOptions,
  ...usRegionOptions,
];

export const dialogTitleByFormStep: Record<FinanceProgramFormSteps, string> = {
  [FinanceProgramFormSteps.First]: 'Program Details',
  [FinanceProgramFormSteps.Second]: 'Configure Structure',
  [FinanceProgramFormSteps.Third]: 'Choose Criteria (Optional)',
  [FinanceProgramFormSteps.Fourth]: 'Submission Settings',
  [FinanceProgramFormSteps.Fifth]: 'Insurance Coverage Requirements',
  [FinanceProgramFormSteps.Sixth]: 'Create Finance Program?',
};

export const insuranceLabelsByKey: InsuranceLabelsByKey = {
  coverageRequirements: 'Coverage Requirements',
  certificateRequirements: 'Certificate Requirements',
  otherInstructions: 'Other Instructions',
};

export const feesMarkupTypesOptions = [
  { label: '$', value: MarkupType.Flat },
  { label: '%', value: MarkupType.Percentage },
];
